import React from 'react';
import {
  DocumentOutlineIcon,
  ClockOutlineIcon,
  DocumentCheckOutlineIcon,
  CheckMarkOutlineIcon,
  CloseOutlineIcon,
} from '@applyboard/ui-icons';
import { Flex, Text, Card as FrameCard, Avatar, Tag } from '@applyboard/crystal-ui';
import { TruncatedButtonLink } from '~/components/uie-fix/TruncatedButtonLink';
import { Attachment } from '~/data/useApplication';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import moment from 'moment';
import { Card } from './Card';
import { Decision } from '@ab/ams-school-api/src/resources/applications/studentDecisions';

type DecisionLetterCardProps = {
  attachmentDocument: Attachment;
  decision?: Decision;
  rejectionReason?: string;
};

function DecisionLetterCard({ attachmentDocument, decision, rejectionReason }: DecisionLetterCardProps) {
  function getDocumentTile(attachmentDocument: Attachment) {
    if (attachmentDocument.type.code === 'REJECTION_LETTER') {
      return 'Decline Letter';
    } else if (attachmentDocument.type.code === 'WAITLIST_LETTER') {
      return 'Waitlist Letter';
    } else if (attachmentDocument.type.code === 'FINAL_OFFER_LETTER') {
      return 'Final Offer Letter';
    } else {
      return 'Initial Offer Letter';
    }
  }

  function getDocumentAvatar(attachmentDocument: Attachment) {
    if (attachmentDocument.isActive === false) {
      return (
        <Avatar
          aria-label="inactive-letter"
          emphasis="highlighted"
          icon={CloseOutlineIcon}
          intent="secondary"
          size="md"
        />
      );
    }
    if (attachmentDocument.type.code === 'REJECTION_LETTER') {
      return (
        <Avatar
          aria-label="rejection-letter"
          emphasis="highlighted"
          icon={CloseOutlineIcon}
          intent="negative"
          size="md"
        />
      );
    } else if (attachmentDocument.type.code === 'WAITLIST_LETTER') {
      return (
        <Avatar
          aria-label="waitlist-letter"
          emphasis="highlighted"
          icon={ClockOutlineIcon}
          intent="secondary"
          size="md"
        />
      );
    } else if (attachmentDocument.type.code === 'FINAL_OFFER_LETTER') {
      return (
        <Avatar
          aria-label="final-offer-letter"
          emphasis="highlighted"
          icon={CheckMarkOutlineIcon}
          intent="positive"
          size="md"
        />
      );
    } else {
      return (
        <Avatar
          aria-label="pre-offer-letter"
          emphasis="highlighted"
          icon={CheckMarkOutlineIcon}
          intent="positive"
          size="md"
        />
      );
    }
  }

  function offerStatusTag(decision: Decision | undefined, attachmentDocument: Attachment) {
    if (attachmentDocument.isActive === false) {
      return (
        <Flex.Item>
          <Text variant="labelS" intent="secondary">
            OFFER STATUS
          </Text>
          {attachmentDocument.isActive === false ? (
            <Tag intent="secondary" size="sm">
              Invalid
            </Tag>
          ) : (
            getDecisionStatusIcon(decision?.studentResponse || 'PENDING')
          )}
        </Flex.Item>
      );
    } else if (attachmentDocument.type.code === 'PRE_OFFER_LETTER' && attachmentDocument.isActive === true) {
      return (
        <Flex.Item>
          <Text variant="labelS" intent="secondary">
            OFFER STATUS
          </Text>
          {getDecisionStatusIcon(decision?.studentResponse || 'PENDING')}
        </Flex.Item>
      );
    }
    return null;
  }
  return (
    <FrameCard>
      <Flex direction="column" gap={8}>
        <Flex direction="row" justify="between" gap={8}>
          <Flex.Item>
            <Flex direction="row" align="center" justify="center" gap={6}>
              <Flex.Item>{getDocumentAvatar(attachmentDocument)}</Flex.Item>
              <Flex.Item>
                <Text variant="titleS" contrast="high" intent="secondary">
                  {getDocumentTile(attachmentDocument)}
                </Text>
              </Flex.Item>
              <Flex.Item>
                <Tag intent={attachmentDocument.isActive === false ? 'secondary' : 'positive'} size="sm">
                  {`Issued on ${moment(attachmentDocument.updatedAt).format('MMM DD, YYYY')}`}
                </Tag>
              </Flex.Item>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <TruncatedButtonLink
              aria-label={`Download ${attachmentDocument.fileName} document`}
              emphasis="highlighted"
              href={attachmentDocument.downloadUrl!}
              intent={attachmentDocument.validationStatus === 'VALID' ? 'positive' : 'primary'}
              size="lg"
              leadIcon={DocumentCheckOutlineIcon}
              width="hug"
              mode="override_user_choice_and_open_in_new_tab"
            >
              {attachmentDocument.fileName}
            </TruncatedButtonLink>
          </Flex.Item>
        </Flex>

        {decision && (
          <Flex direction="row" gap={36} justify="start">
            <Flex.Item>{offerStatusTag(decision, attachmentDocument)}</Flex.Item>
            {decision?.decisionDate &&
              decision.studentResponse === 'ACCEPTED' &&
              attachmentDocument.isActive !== false && (
                <Flex.Item>
                  <Text variant="labelS" intent="secondary">
                    ACCEPTED ON
                  </Text>
                  <Tag intent="secondary" size="sm">
                    {moment(decision.decisionDate).format('MMM DD, YYYY')}
                  </Tag>
                </Flex.Item>
              )}
            {decision?.decisionDate &&
              decision.studentResponse === 'REJECTED' &&
              attachmentDocument.isActive !== false && (
                <Flex.Item>
                  <Text variant="labelS" intent="secondary">
                    REJECTED ON
                  </Text>
                  <Tag intent="secondary" size="sm">
                    {moment(decision.decisionDate).format('MMM DD, YYYY')}
                  </Tag>
                </Flex.Item>
              )}

            {decision?.reason && (
              <Flex.Item>
                <Text variant="labelS" intent="secondary">
                  REASON OF REJECTION
                </Text>
                <Text variant="labelM" intent="secondary" contrast="low">
                  {decision?.reason}
                </Text>
              </Flex.Item>
            )}
          </Flex>
        )}

        {attachmentDocument.type.code === 'REJECTION_LETTER' && (
          <Flex direction="row" gap={36} justify="start">
            <Flex.Item>
              <Text variant="labelS" intent="secondary">
                School Declination Reason
              </Text>
              <Text variant="labelM" intent="secondary" contrast="low">
                {rejectionReason}
              </Text>
            </Flex.Item>
          </Flex>
        )}
      </Flex>
    </FrameCard>
  );
}
export function DecisionLetters() {
  const {
    application: {
      preOfferLetters,
      rejectionLetters,
      studentDecisions,
      waitlistLetters,
      finalOfferLetters,
      rejectionReason,
    },
  } = useAppProcessing();

  const preOfferLettersArray = Array.isArray(preOfferLetters) ? preOfferLetters : [];
  const finalOfferLettersArray = Array.isArray(finalOfferLetters) ? finalOfferLetters : [];
  const waitlistLetterArray = Array.isArray(waitlistLetters) ? waitlistLetters : [];
  const rejectionLetterArray = Array.isArray(rejectionLetters) ? rejectionLetters : [];
  const combinedLetters = [
    ...preOfferLettersArray,
    ...finalOfferLettersArray,
    ...waitlistLetterArray,
    ...rejectionLetterArray,
  ];
  const activeLetters = combinedLetters
    .filter((l) => l.isActive !== false)
    .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime());
  const inactiveLetters = combinedLetters
    .filter((l) => l.isActive === false)
    .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime());

  function getDecisionForLetter(letter: Attachment): Decision | undefined {
    let decision;
    if (letter.type.code === 'PRE_OFFER_LETTER') {
      decision = studentDecisions?.preOfferDecision;
    } else if (letter.type.code === 'FINAL_OFFER_LETTER') {
      decision = studentDecisions?.finalOfferDecision;
    }
    if (decision) {
      return {
        ...decision,
        decisionDate: decision.decisionDate ? decision.decisionDate : undefined,
      };
    }
    return undefined;
  }
  return (
    <Card hideChevron id="decisionLetters" icon={DocumentOutlineIcon} title="Decision Letters">
      <Flex gap={8} direction="column">
        {[...activeLetters, ...inactiveLetters].map((offerLetter, idx) => (
          <Flex.Item key={`${offerLetter.type.code}-${offerLetter.updatedAt}-${idx}`}>
            <DecisionLetterCard
              attachmentDocument={offerLetter}
              decision={getDecisionForLetter(offerLetter)}
              rejectionReason={rejectionReason}
            />
          </Flex.Item>
        ))}
      </Flex>
    </Card>
  );
}

const getDecisionStatusIcon = (decisionStatus: string) => {
  switch (decisionStatus) {
    case 'ACCEPTED':
      return (
        <Tag intent="positive" size="sm">
          Student Accepted Offer
        </Tag>
      );
    case 'REJECTED':
      return (
        <Tag intent="negative" size="sm">
          Student Rejected Offer
        </Tag>
      );
    case 'PENDING':
      return (
        <Tag intent="warning" size="sm">
          Acceptance Pending
        </Tag>
      );
    default:
      return;
  }
};
